import { useCallback } from "react";

import { type AWSNodeConfigApiParameters } from "@doitintl/cmp-models";

import { useApiContext } from "../../../../../api/context";
import { useCustomerId } from "../../../../../Components/hooks/useCustomerId";
import { type AWSAccount } from "../../../../Settings/AWS/types";
import { getApiServiceDescriptor } from "../../utils/getApiServiceDescriptor";

type AWSPermissions = {
  [permissionsNamespace: string]: string[];
};

type GrantedAWSPermissions = {
  awsPermissions: AWSPermissions;
  hasPolicy: boolean;
};

export type AwsPermissionsCheckResult = {
  permissionExists: boolean;
  awsPermission: string;
  commandToGrantPermission: string;
};

function generateCreatePolicyCommand(roleName: string, policyName: string, awsPermission: string) {
  return `aws iam put-role-policy \\
--role-name ${roleName} \\
--policy-name ${policyName} \\
--policy-document '${JSON.stringify({
    Version: "2012-10-17",
    Statement: [
      {
        Action: [awsPermission],
        Resource: "*",
        Effect: "Allow",
      },
    ],
  })}'`;
}

function generateUpdatePolicyCommand(roleName: string, policyName: string, awsPermission: string) {
  return `aws iam put-role-policy \\
--role-name ${roleName} \\
--policy-name ${policyName} \\
--policy-document "$(aws iam get-role-policy --role-name ${roleName} --policy-name ${policyName} --output json | jq '. | {Statement: .PolicyDocument.Statement, Version: .PolicyDocument.Version} | .Statement[0].Action += ["${awsPermission}"]'  | jq -c)"`;
}

export function isAwsPermissionGranted(
  awsPermissions: AWSPermissions,
  permissionsNamespace: string,
  operationName: string
): boolean {
  if (awsPermissions["*"]?.includes("*")) {
    return true;
  }

  const permissions = awsPermissions[permissionsNamespace] || [];
  return permissions.some((perm) => {
    const pattern = `^${perm.replace(/\*/g, ".*")}$`;
    return new RegExp(pattern, "i").test(operationName);
  });
}

export function useAWSPermissionsCheck(cloudflowId: string) {
  const customerId = useCustomerId();
  const api = useApiContext();

  return useCallback(
    async (parameters: AWSNodeConfigApiParameters, account: AWSAccount): Promise<AwsPermissionsCheckResult> => {
      const accountId = parameters.configurationValues.accountId;

      const [
        serviceDescriptor,
        {
          data: { awsPermissions, hasPolicy },
        },
      ] = await Promise.all([
        getApiServiceDescriptor(parameters.operation.provider, parameters.operation.service),
        api.request<GrantedAWSPermissions>({
          method: "GET",
          url: `/v1/customers/${customerId}/cloudflow/get-aws-permissions`,
          params: {
            accountId,
            cloudflowId,
          },
        }),
      ]);

      if (serviceDescriptor.permissionsNamespace === undefined) {
        throw new Error(`Missing permissions namespace for service: ${serviceDescriptor.serviceName}`);
      }

      const awsPermission = `${serviceDescriptor.permissionsNamespace}:${parameters.operation.id}`;

      const permissionExists = isAwsPermissionGranted(
        awsPermissions,
        serviceDescriptor.permissionsNamespace,
        parameters.operation.id
      );

      let commandToGrantPermission = "";

      if (!permissionExists) {
        const role = account.roleName;
        const policyName = `DoiT-CloudFlow-${cloudflowId}`;
        commandToGrantPermission = hasPolicy
          ? generateUpdatePolicyCommand(role, policyName, awsPermission)
          : generateCreatePolicyCommand(role, policyName, awsPermission);
      }

      return {
        awsPermission,
        permissionExists,
        commandToGrantPermission,
      };
    },
    [api, customerId, cloudflowId]
  );
}

export const useCheckAllAccountPermissions = (cloudflowId: string) => {
  const checkPermission = useAWSPermissionsCheck(cloudflowId);

  return useCallback(
    async (parameters: AWSNodeConfigApiParameters, accounts: AWSAccount[]) => {
      const checkedPermissions = await Promise.all(
        accounts.map(async (account) => {
          const result = await checkPermission(
            {
              ...parameters,
              configurationValues: { ...parameters.configurationValues, accountId: account.accountId },
            },
            account
          );

          return {
            accountName: `${account.accountId}${account.properties?.name ? ` (${account.properties.name})` : ""}`,
            checkResult: result,
          };
        })
      );

      return checkedPermissions;
    },
    [checkPermission]
  );
};
